/**
 * The add / edit form for one PF weld. Weld numbers are unique across the
 * company (409 `weld_number_taken`). The NDT report FILE is not on this form —
 * it is uploaded on the weld's page after the weld is saved (the API refuses
 * `ndt_report_upload_id` on a create).
 */
import React, { useState } from 'react';
import type { PfCatalog, PfMeta } from '../../../types/pipeline-facility';
import type { PfWeld, PfWeldWrite } from '../../../types/pf-digs';
import { blankToNull, toInput } from '../../../lib/pipeline-facility';
import { numOrNull } from '../../../lib/pf-digs';
import { PF_DATE_INPUT, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfField, PfFormError } from '../PfBits';
import { PfFormGrid, unitLabel } from './DigBits';

export function WeldForm({
  weld,
  repairs,
  defaultRepairId,
  catalog,
  meta,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  weld?: PfWeld | null;
  /** The job's dig repairs a weld may belong to. */
  repairs: { id: string; label: string }[];
  defaultRepairId?: string;
  catalog: PfCatalog | null;
  meta: PfMeta | null;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: PfWeldWrite) => void;
}) {
  const [f, setF] = useState({
    weld_number: weld?.weldNumber ?? '',
    dig_repair_id: weld?.digRepairId ?? defaultRepairId ?? '',
    chainage: toInput(weld?.chainage),
    heat_numbers: weld?.heatNumbers ?? '',
    welder_name: weld?.welderName ?? '',
    welder_stamp: weld?.welderStamp ?? '',
    wps_ref: weld?.wpsRef ?? '',
    weld_date: weld?.weldDate ?? '',
    ndt_company: weld?.ndtCompany ?? '',
    ndt_result: weld?.ndtResult ?? '',
    ndt_report_ref: weld?.ndtReportRef ?? '',
    notes: weld?.notes ?? '',
  });
  const [local, setLocal] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocal(null);
    if (!f.weld_number.trim()) return setLocal('Enter the weld number.');
    const chainage = numOrNull(f.chainage);
    if (chainage !== null && !Number.isFinite(chainage)) return setLocal('Chainage must be a number.');
    const body: PfWeldWrite = {
      weld_number: f.weld_number.trim(),
      dig_repair_id: f.dig_repair_id || null,
      chainage,
      heat_numbers: blankToNull(f.heat_numbers),
      welder_name: blankToNull(f.welder_name),
      welder_stamp: blankToNull(f.welder_stamp),
      wps_ref: blankToNull(f.wps_ref),
      weld_date: f.weld_date || null,
      ndt_company: blankToNull(f.ndt_company),
      ndt_result: f.ndt_result || null,
      ndt_report_ref: blankToNull(f.ndt_report_ref),
      notes: blankToNull(f.notes),
    };
    if (!weld) {
      (Object.keys(body) as (keyof PfWeldWrite)[]).forEach((k) => {
        if (body[k] === null) delete body[k];
      });
    }
    onSubmit(body);
  };
  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="pf-weld-form">
      <PfFormError message={local ?? error} />
      <PfFormGrid>
        <PfField label="Weld number">
          <input className={PF_INPUT} aria-label="Weld number" value={f.weld_number} onChange={set('weld_number')} />
        </PfField>
        <PfField label="Dig repair">
          <select className={PF_INPUT} aria-label="Dig repair" value={f.dig_repair_id} onChange={set('dig_repair_id')}>
            <option value="">Not on a dig repair</option>
            {repairs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label={unitLabel('Chainage', meta?.units?.chainage)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="Weld chainage" value={f.chainage} onChange={set('chainage')} />
        </PfField>
        <PfField label="Welder">
          <input className={PF_INPUT} aria-label="Welder name" value={f.welder_name} onChange={set('welder_name')} />
        </PfField>
        <PfField label="Welder stamp">
          <input className={PF_INPUT} aria-label="Welder stamp" value={f.welder_stamp} onChange={set('welder_stamp')} />
        </PfField>
        <PfField label="WPS">
          <input className={PF_INPUT} aria-label="WPS reference" value={f.wps_ref} onChange={set('wps_ref')} />
        </PfField>
        <PfField label="Heat numbers">
          <input className={PF_INPUT} aria-label="Heat numbers" value={f.heat_numbers} onChange={set('heat_numbers')} />
        </PfField>
        <PfField label="Weld date">
          <input type="date" className={PF_DATE_INPUT} aria-label="Weld date" value={f.weld_date} onChange={set('weld_date')} />
        </PfField>
        <PfField label="NDT company">
          <input className={PF_INPUT} aria-label="NDT company" value={f.ndt_company} onChange={set('ndt_company')} />
        </PfField>
        <PfField label="NDT result">
          <select className={PF_INPUT} aria-label="NDT result" value={f.ndt_result} onChange={set('ndt_result')}>
            <option value="">Not recorded</option>
            {(catalog?.weldNdtResults ?? []).map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="NDT report reference">
          <input className={PF_INPUT} aria-label="NDT report reference" value={f.ndt_report_ref} onChange={set('ndt_report_ref')} />
        </PfField>
      </PfFormGrid>
      <PfField label="Notes">
        <textarea className={`${PF_INPUT} h-16 py-2`} aria-label="Weld notes" value={f.notes} onChange={set('notes')} />
      </PfField>
      <div className="flex justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={PF_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
